import { zodResolver } from "@hookform/resolvers/zod";
import { addDays, format } from "date-fns";
import {
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MailIcon,
  MinusIcon,
  SearchIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { type SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import type { z } from "zod";
import { SubmitButton } from "@/components/buttons/submit-button";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Timeline,
  TimelineContent,
  TimelineHeader,
  TimelineIndicator,
  TimelineItem,
  TimelineSeparator,
  TimelineTitle,
} from "@/components/ui/timeline";
import { useDebounce } from "@/hooks/use-debounce";
import { useSession } from "@/hooks/use-session";
import {
  ADDRESS_CATEGORIES,
  CITIZENSHIP_OPTIONS,
  FULFILLMENT_TYPES,
  SALUTATION_OPTIONS,
} from "@/lib/constants";
import { cn } from "@/lib/utils";
import {
  useGetCountriesQuery,
  useGetPublicHolidaysQuery,
  useGetStoresQuery,
  useLazyGetAddressesQuery,
  useLazyGetPostalCodesQuery,
} from "@/redux/services/order-portal";
import { setOrder } from "@/redux/slices/order";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { customerInformationSchema } from "@/shared/schemas/customer-information";

export const CustomerInformationForm = ({
  serviceType,
  className,
}: {
  serviceType: ServiceType;
  className?: string;
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Necessary
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { session } = useSession();

  const [isDeliveryAddressCommandOpen, setIsDeliveryAddressCommandOpen] =
    useState<boolean>(false);
  const [isBillingAddressCommandOpen, setIsBillingAddressCommandOpen] =
    useState<boolean>(false);
  const [deliveryAddressQuery, setDeliveryAddressQuery] = useState<string>("");
  const [billingAddressQuery, setBillingAddressQuery] = useState<string>("");

  const order = useAppSelector((state) => state.order);
  const dispatch = useAppDispatch();
  const { data: countries } = useGetCountriesQuery({
    // customer_type should be non-null at this order step
    customer_type: order.customerType as CustomerType,
  });
  const { data: stores } = useGetStoresQuery({
    // customer_type should be non-null at this order step
    customer_type: order.customerType as CustomerType,
  });
  const { data: publicHolidays } = useGetPublicHolidaysQuery({
    year: new Date().getFullYear(),
  });

  const form = useForm<z.infer<typeof customerInformationSchema>>({
    resolver: zodResolver(customerInformationSchema),
    defaultValues: {
      // customer_type should be non-null at this order step
      customerType: order.customerType as CustomerType,
      salutation: order.salutation ?? "Mr",
      gender: order.gender ?? "M",
      name: order.name ?? "",
      nricNumber: order.nricNumber ?? "",
      nricExpirationDate: order.nricExpirationDate ?? undefined,
      dateOfArrival: order.dateOfArrival ?? undefined,
      flightNumber: order.flightNumber ?? undefined,
      email: order.email ?? "",
      mobileNumber: order.mobileNumber ?? undefined,
      homeNumber: order.homeNumber ?? undefined,
      officeNumber: order.officeNumber ?? undefined,
      dateOfBirth: order.dateOfBirth ?? undefined,
      citizenship: order.citizenship ?? undefined,
      country: order.country?.id ?? undefined,
      fulfilmentType: order.fulfilmentType ?? undefined,
      fulfilmentDate: order.fulfilmentDate ?? undefined,
      store: order.store?.id ? order.store.id.toString() : undefined,
      deliveryAddress: order.deliveryAddress ?? undefined,
      isSameAsDeliveryAddress:
        serviceType !== "tourist" && serviceType !== "easi"
          ? order.isSameAsDeliveryAddress
          : undefined,
      billingAddress: order.billingAddress ?? undefined,
    },
  });

  const selectedCitizenship = useWatch({
    control: form.control,
    name: "citizenship",
  });
  const selectedFulfilmentType = useWatch({
    control: form.control,
    name: "fulfilmentType",
  });
  const selectedStoreId = useWatch({
    control: form.control,
    name: "store",
  });
  const deliveryAddressCategory = useWatch({
    control: form.control,
    name: "deliveryAddress.addressCategory",
  });
  const selectedDeliveryVillage = useWatch({
    control: form.control,
    name: "deliveryAddress.village",
  });
  const selectedDeliveryPostalCode = useWatch({
    control: form.control,
    name: "deliveryAddress.postalCode",
  });
  const isSameAsDeliveryAddress = useWatch({
    control: form.control,
    name: "isSameAsDeliveryAddress",
  });
  const billingAddressCategory = useWatch({
    control: form.control,
    name: "billingAddress.addressCategory",
  });
  const selectedBillingVillage = useWatch({
    control: form.control,
    name: "billingAddress.village",
  });
  const selectedBillingPostalCode = useWatch({
    control: form.control,
    name: "billingAddress.postalCode",
  });

  const [
    getDeliveryAddresses,
    { data: deliveryAddresses, currentData: currentDeliveryAddresses },
  ] = useLazyGetAddressesQuery();
  const [
    getBillingAddresses,
    { data: billingAddresses, currentData: currentBillingAddresses },
  ] = useLazyGetAddressesQuery();
  const [getDeliveryPostalCodes, { currentData: deliveryPostalCodes }] =
    useLazyGetPostalCodesQuery();
  const [getBillingPostalCodes, { currentData: billingPostalCodes }] =
    useLazyGetPostalCodesQuery();

  useEffect(() => {
    getDeliveryAddresses({
      // customer_type should be non-null at this order step
      customer_type: order.customerType as CustomerType,
      address: deliveryAddressQuery,
      category: deliveryAddressCategory ?? "All",
    });

    getBillingAddresses({
      // customer_type should be non-null at this order step
      customer_type: order.customerType as CustomerType,
      address: billingAddressQuery,
      category: billingAddressCategory ?? "All",
    });
  }, []);

  useEffect(() => {
    if (selectedDeliveryVillage) {
      getDeliveryPostalCodes({
        // customer_type should be non-null at this order step
        customer_type: order.customerType as CustomerType,
        village_id: Number(selectedDeliveryVillage),
      });
    }
  }, [selectedDeliveryVillage]);

  useEffect(() => {
    if (selectedBillingVillage) {
      getBillingPostalCodes({
        // customer_type should be non-null at this order step
        customer_type: order.customerType as CustomerType,
        village_id: Number(selectedBillingVillage),
      });
    }
  }, [selectedBillingVillage]);

  useDebounce(
    () => {
      if (deliveryAddressQuery) {
        getDeliveryAddresses({
          // customer_type should be non-null at this order step
          customer_type: order.customerType as CustomerType,
          address: deliveryAddressQuery,
          category: deliveryAddressCategory ?? "All",
        });
      }
    },
    1000,
    [deliveryAddressQuery]
  );

  useDebounce(
    () => {
      if (billingAddressQuery) {
        getBillingAddresses({
          // customer_type should be non-null at this order step
          customer_type: order.customerType as CustomerType,
          address: billingAddressQuery,
          category: billingAddressCategory ?? "All",
        });
      }
    },
    1000,
    [billingAddressQuery]
  );

  function getSelectedStore() {
    return stores?.find((store) => store.id.toString() === selectedStoreId);
  }

  function getSelectedDeliveryAddress() {
    const selectedAddressObject =
      currentDeliveryAddresses?.find(
        (addr) => addr.village.toString() === selectedDeliveryVillage
      ) ||
      deliveryAddresses?.find(
        (addr) => addr.village.toString() === selectedDeliveryVillage
      );

    return selectedAddressObject;
  }

  function getSelectedBillingAddress() {
    const selectedAddressObject =
      currentBillingAddresses?.find(
        (addr) => addr.village.toString() === selectedBillingVillage
      ) ||
      billingAddresses?.find(
        (addr) => addr.village.toString() === selectedBillingVillage
      );

    return selectedAddressObject;
  }

  const onSubmit: SubmitHandler<z.infer<typeof customerInformationSchema>> = (
    data
  ) => {
    try {
      if (data.fulfilmentType === "Instant Delivery") {
        data.deliveryAddress = data.billingAddress;
      }

      if (data.isSameAsDeliveryAddress) {
        data.billingAddress = data.deliveryAddress;
      }

      const updatedOrder = {
        ...order,
        ...data,
        store: getSelectedStore(),
        country: {
          id: data.country ?? undefined,
          name:
            countries?.find(
              (country) => country.value.toString() === data.country
            )?.label ?? undefined,
        },
      };

      dispatch(setOrder(updatedOrder));

      navigate(`/${serviceType}/summary`);

      form.reset();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "An unknown error occurred"
      );
    }
  };

  return (
    <Form {...form}>
      <form className={className} onSubmit={form.handleSubmit(onSubmit)}>
        <Timeline defaultValue={3}>
          <TimelineItem step={1}>
            <TimelineHeader>
              <TimelineSeparator className="group-data-[orientation=vertical]/timeline:h-[calc(100%-1.5rem-0.25rem)] group-data-[orientation=vertical]/timeline:translate-y-6.5" />
              <TimelineTitle className="-mt-1">
                {t("customer-information.titles.customer-details")}
              </TimelineTitle>
              <TimelineIndicator className="flex size-6 items-center justify-center">
                <MinusIcon className="size-4" />
              </TimelineIndicator>
            </TimelineHeader>
            <TimelineContent className="flex flex-col gap-6 sm:gap-4">
              <div className="flex flex-col gap-6 sm:flex-row sm:gap-4">
                <FormField
                  control={form.control}
                  name="salutation"
                  render={({ field }) => (
                    <FormItem className="sm:basis-1/9">
                      <FormLabel className="sr-only">
                        {t("labels.salutation")}
                      </FormLabel>
                      <Select
                        defaultValue={field.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                          form.setValue("gender", value === "Mr" ? "M" : "F");
                        }}
                        {...field}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={`${t("labels.salutation")}*`}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {SALUTATION_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="sm:basis-3/9">
                      <FormLabel className="sr-only">
                        {serviceType === "tourist"
                          ? t("labels.passport-full-name")
                          : t("labels.full-name")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder={
                            serviceType === "tourist"
                              ? `${t("labels.passport-full-name")}*`
                              : `${t("labels.full-name")}*`
                          }
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="nricNumber"
                  render={({ field }) => (
                    <FormItem className="sm:basis-3/9">
                      <FormLabel className="sr-only">
                        {serviceType === "tourist"
                          ? t("labels.passport-number")
                          : t("labels.nric-number")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          disabled={!!order.nricNumber}
                          placeholder={
                            serviceType === "tourist"
                              ? `${t("labels.passport-number")}*`
                              : `${t("labels.nric-number")}*`
                          }
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="nricExpirationDate"
                  render={({ field }) => (
                    <FormItem className="sm:basis-1/9">
                      <FormLabel className="sr-only">
                        {serviceType === "tourist"
                          ? t("labels.passport-expiration-date")
                          : t("labels.nric-expiration-date")}
                      </FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              className={cn(
                                !field.value && "text-muted-foreground"
                              )}
                              variant="outline"
                            >
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>
                                  {serviceType === "tourist"
                                    ? `${t("labels.passport-expiration-date")}*`
                                    : `${t("labels.nric-expiration-date")}*`}
                                </span>
                              )}
                              <CalendarIcon className="ml-auto" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-auto p-0">
                          <Calendar
                            autoFocus
                            disabled={{ before: new Date() }}
                            mode="single"
                            onSelect={field.onChange}
                            selected={field.value}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {serviceType === "tourist" && (
                <div className="flex flex-col gap-6 sm:flex-row sm:gap-4">
                  <FormField
                    control={form.control}
                    name="dateOfArrival"
                    render={({ field }) => (
                      <FormItem className="sm:basis-1/2">
                        <FormLabel className="sr-only">
                          {t("labels.date-of-arrival")}
                        </FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                className={cn(
                                  !field.value && "text-muted-foreground"
                                )}
                                variant="outline"
                              >
                                {field.value ? (
                                  format(field.value, "PPP")
                                ) : (
                                  <span>{t("labels.date-of-arrival")}*</span>
                                )}
                                <CalendarIcon className="ml-auto" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-auto p-0">
                            <Calendar
                              autoFocus
                              disabled={{ before: addDays(new Date(), 1) }}
                              mode="single"
                              onSelect={field.onChange}
                              selected={field.value}
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="flightNumber"
                    render={({ field }) => (
                      <FormItem className="sm:basis-1/2">
                        <FormLabel className="sr-only">
                          {t("labels.flight-number")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder={`${t("labels.flight-number")}*`}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <div className="flex flex-col gap-6 sm:flex-row sm:gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem
                      className={cn(
                        "sm:basis-1/4",
                        serviceType === "tourist" && "sm:basis-1/2"
                      )}
                    >
                      <FormLabel className="sr-only">
                        {t("labels.email")}
                      </FormLabel>
                      <FormControl>
                        <InputGroup>
                          <InputGroupInput
                            placeholder={`${t("labels.email")}*`}
                            {...field}
                          />
                          <InputGroupAddon align="inline-end">
                            <MailIcon />
                          </InputGroupAddon>
                        </InputGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {serviceType !== "tourist" && (
                  <>
                    <FormField
                      control={form.control}
                      name="mobileNumber"
                      render={({ field }) => (
                        <FormItem className="sm:basis-1/4">
                          <FormLabel className="sr-only">
                            {t("labels.mobile-number")}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={`${t("labels.mobile-number")}*`}
                              type="tel"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="homeNumber"
                      render={({ field }) => (
                        <FormItem className="sm:basis-1/4">
                          <FormLabel className="sr-only">
                            {t("labels.land-line-number")}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t("labels.land-line-number")}
                              type="tel"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="officeNumber"
                      render={({ field }) => (
                        <FormItem className="sm:basis-1/4">
                          <FormLabel className="sr-only">
                            {t("labels.office-number")}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t("labels.office-number")}
                              type="tel"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
              </div>

              {serviceType !== "tourist" && (
                <div className="flex flex-col gap-6 sm:flex-row sm:gap-4">
                  <FormField
                    control={form.control}
                    name="dateOfBirth"
                    render={({ field }) => (
                      <FormItem className="sm:basis-1/3">
                        <FormLabel className="sr-only">
                          {t("labels.date-of-birth")}
                        </FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                className={cn(
                                  !field.value && "text-muted-foreground"
                                )}
                                variant="outline"
                              >
                                {field.value ? (
                                  format(field.value, "PPP")
                                ) : (
                                  <span>{t("labels.date-of-birth")}*</span>
                                )}
                                <CalendarIcon className="ml-auto" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-auto p-0">
                            <Calendar
                              autoFocus
                              disabled={(date) => date > new Date()}
                              mode="single"
                              onSelect={field.onChange}
                              selected={field.value}
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="citizenship"
                    render={({ field }) => (
                      <FormItem className="sm:basis-1/3">
                        <FormLabel className="sr-only">
                          {t("labels.citizenship")}
                        </FormLabel>
                        <Select
                          defaultValue={field.value}
                          onValueChange={(value) => {
                            field.onChange(value);

                            if (value === "Brunei Darussalam") {
                              if (
                                order.customerType === "MOBI-POSTPAID" ||
                                order.customerType === "FREEDOM"
                              ) {
                                form.setValue("country", "BN");
                              } else {
                                form.setValue("country", "1");
                              }
                            } else {
                              form.setValue("country", undefined);
                            }
                          }}
                          {...field}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue
                                placeholder={`${t("labels.citizenship")}*`}
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {CITIZENSHIP_OPTIONS.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {selectedCitizenship !== "Brunei Darussalam" && (
                    <FormField
                      control={form.control}
                      name="country"
                      render={({ field }) => (
                        <FormItem className="sm:basis-1/3">
                          <FormLabel className="sr-only">
                            {t("labels.country")}
                          </FormLabel>
                          <Select
                            defaultValue={field.value}
                            onValueChange={field.onChange}
                            {...field}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={`${t("placeholders.country")}*`}
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {countries?.map((country) => (
                                <SelectItem
                                  key={country.value}
                                  value={country.value.toString()}
                                >
                                  {country.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              )}
            </TimelineContent>
          </TimelineItem>

          <TimelineItem step={2}>
            <TimelineHeader>
              <TimelineSeparator className="group-data-[orientation=vertical]/timeline:h-[calc(100%-1.5rem-0.25rem)] group-data-[orientation=vertical]/timeline:translate-y-6.5" />
              <TimelineTitle className="-mt-1">
                {serviceType === "tourist"
                  ? t("customer-information.titles.pick-up-address")
                  : // biome-ignore lint/style/noNestedTernary: Different titles for different service types
                    serviceType === "infinity"
                    ? t("customer-information.titles.service-address")
                    : t("customer-information.titles.delivery-pick-up")}
              </TimelineTitle>
              <TimelineIndicator className="flex size-6 items-center justify-center">
                <MinusIcon className="size-4" />
              </TimelineIndicator>
            </TimelineHeader>
            <TimelineContent className="flex flex-col gap-6 sm:gap-4">
              {serviceType !== "infinity" && serviceType !== "tourist" && (
                <>
                  <div className="flex flex-col gap-6 sm:flex-row sm:gap-4">
                    <FormField
                      control={form.control}
                      name="fulfilmentType"
                      render={({ field }) => (
                        <FormItem className="sm:basis-1/3">
                          <FormLabel className="sr-only">
                            {t("labels.fulfillment-type")}
                          </FormLabel>
                          <Select
                            defaultValue={field.value}
                            onValueChange={(value) => {
                              field.onChange(value);

                              if (
                                value === "Instant Delivery" ||
                                value === "Delivery"
                              ) {
                                form.setValue("store", undefined);
                              }

                              if (value === "Pick Up") {
                                form.setValue("deliveryAddress", undefined);
                              }
                            }}
                            {...field}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={`${t(
                                    "placeholders.fulfilment-type"
                                  )}*`}
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {FULFILLMENT_TYPES.map((type) => (
                                <SelectItem
                                  className={cn(
                                    type.requireLogin && !session && "hidden"
                                  )}
                                  key={type.label}
                                  value={type.value}
                                >
                                  {t(`labels.${type.label}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {publicHolidays && (
                      <FormField
                        control={form.control}
                        name="fulfilmentDate"
                        render={({ field }) => (
                          <FormItem className="sm:basis-1/3">
                            <FormLabel className="sr-only">
                              {t("labels.fulfilment-date")}
                            </FormLabel>
                            <Popover>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    className={cn(
                                      !field.value && "text-muted-foreground"
                                    )}
                                    disabled={!selectedFulfilmentType}
                                    variant="outline"
                                  >
                                    {field.value ? (
                                      format(field.value, "PPP")
                                    ) : (
                                      <span>
                                        {t("labels.fulfilment-date")}*
                                      </span>
                                    )}
                                    <CalendarIcon className="ml-auto" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent
                                align="start"
                                className="w-auto p-0"
                              >
                                <Calendar
                                  autoFocus
                                  disabled={[
                                    {
                                      before:
                                        selectedFulfilmentType ===
                                        "Instant Delivery"
                                          ? new Date()
                                          : // biome-ignore lint/style/noNestedTernary: Cleanest option
                                            selectedFulfilmentType ===
                                              "Delivery"
                                            ? addDays(new Date(), 2)
                                            : addDays(new Date(), 1),
                                    },
                                    { dayOfWeek: [0] },
                                    ...(publicHolidays || []),
                                  ]}
                                  mode="single"
                                  onSelect={field.onChange}
                                  selected={field.value}
                                />
                              </PopoverContent>
                            </Popover>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </div>

                  <Separator
                    className={cn(
                      selectedFulfilmentType === "Instant Delivery" && "hidden"
                    )}
                  />
                </>
              )}

              {(serviceType === "tourist" ||
                selectedFulfilmentType === "Pick Up") && (
                <div className="flex flex-col gap-6 sm:flex-row sm:gap-4">
                  <FormField
                    control={form.control}
                    name="store"
                    render={({ field }) => (
                      <>
                        <FormItem className="sm:basis-1/3">
                          <FormLabel className="sr-only">
                            {t("labels.store")}
                          </FormLabel>
                          <Select
                            defaultValue={field.value}
                            onValueChange={field.onChange}
                            {...field}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={`${t("placeholders.store")}*`}
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {stores?.map((store) => (
                                <SelectItem
                                  key={store.id}
                                  value={store.id.toString()}
                                >
                                  {store.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>

                        <div className="sm:basis-1/3">
                          <p className="font-medium">
                            {getSelectedStore()?.address}
                          </p>
                        </div>
                      </>
                    )}
                  />
                </div>
              )}

              {(serviceType === "infinity" ||
                (serviceType !== "tourist" &&
                  selectedFulfilmentType === "Delivery")) && (
                <>
                  <div className="flex flex-col gap-6 sm:flex-row sm:gap-4">
                    <FormField
                      control={form.control}
                      name="deliveryAddress.addressCategory"
                      render={({ field }) => (
                        <FormItem className="sm:basis-1/4">
                          <FormLabel className="sr-only">
                            {t("labels.address-category")}
                          </FormLabel>
                          <Select
                            defaultValue={field.value}
                            onValueChange={field.onChange}
                            {...field}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={`${t(
                                    "placeholders.address-category"
                                  )}*`}
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {ADDRESS_CATEGORIES.map((category) => (
                                <SelectItem key={category} value={category}>
                                  {category}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="deliveryAddress.village"
                      render={({ field }) => {
                        const selectedAddressObject =
                          getSelectedDeliveryAddress();
                        const displayValue = selectedAddressObject
                          ? selectedAddressObject.address
                          : field.value || "";

                        return (
                          <FormItem className="flex flex-col sm:basis-1/2">
                            <FormLabel className="sr-only">
                              {t("labels.village")}
                            </FormLabel>
                            <Popover
                              onOpenChange={(open) => {
                                setIsDeliveryAddressCommandOpen(open);
                                if (!open) {
                                  setDeliveryAddressQuery("");
                                }
                              }}
                              open={isDeliveryAddressCommandOpen}
                            >
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <InputGroup>
                                    <InputGroupInput
                                      className="w-full pr-8"
                                      onChange={(e) => {
                                        if (isDeliveryAddressCommandOpen) {
                                          setDeliveryAddressQuery(
                                            e.target.value
                                          );
                                        }
                                      }}
                                      onClick={() => {
                                        if (!isDeliveryAddressCommandOpen) {
                                          setIsDeliveryAddressCommandOpen(true);
                                        }
                                      }}
                                      placeholder={`${t(
                                        "placeholders.village"
                                      )}*`}
                                      readOnly={!isDeliveryAddressCommandOpen}
                                      value={
                                        isDeliveryAddressCommandOpen
                                          ? deliveryAddressQuery
                                          : displayValue
                                      }
                                    />
                                    <InputGroupAddon align="inline-end">
                                      {isDeliveryAddressCommandOpen ? (
                                        <SearchIcon className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground" />
                                      ) : (
                                        <ChevronDownIcon className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground" />
                                      )}
                                    </InputGroupAddon>
                                  </InputGroup>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent
                                align="start"
                                className="max-h-64 w-[--radix-popover-trigger-width] overflow-y-auto overflow-x-hidden p-0"
                                onOpenAutoFocus={(e) => e.preventDefault()}
                              >
                                {currentDeliveryAddresses &&
                                currentDeliveryAddresses.length > 0 ? (
                                  currentDeliveryAddresses.map((address) => (
                                    <Button
                                      className="flex h-auto w-full items-center justify-start px-3 py-2 text-left"
                                      key={`${address.district}-${address.mukim}-${address.village}`}
                                      onClick={() => {
                                        form.setValue(
                                          "deliveryAddress.village",
                                          address.village.toString()
                                        );
                                        form.setValue(
                                          "deliveryAddress.mukim",
                                          address.mukim.toString()
                                        );
                                        form.setValue(
                                          "deliveryAddress.district",
                                          address.district.toString()
                                        );
                                        form.setValue(
                                          "deliveryAddress.address",
                                          address.address
                                        );
                                        setIsDeliveryAddressCommandOpen(false);
                                        setDeliveryAddressQuery("");
                                      }}
                                      variant="ghost"
                                    >
                                      <span className="truncate">
                                        {address.address}
                                      </span>
                                    </Button>
                                  ))
                                  // biome-ignore lint/style/noNestedTernary: Cleanest option
                                ) : deliveryAddressQuery ? (
                                  <div className="p-4 text-center text-muted-foreground text-sm">
                                    {t("placeholders.no-results")}
                                  </div>
                                ) : (
                                  <div className="p-4 text-center text-muted-foreground text-sm">
                                    {t("placeholders.search-address")}
                                  </div>
                                )}
                              </PopoverContent>
                            </Popover>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={form.control}
                      name="deliveryAddress.postalCode"
                      render={({ field }) => (
                        <FormItem
                          className={cn(
                            selectedDeliveryPostalCode === "Other"
                              ? "sm:basis-1/8"
                              : "sm:basis-1/4"
                          )}
                        >
                          <FormLabel className="sr-only">
                            {t("labels.postal-code")}
                          </FormLabel>
                          <Select
                            defaultValue={field.value}
                            disabled={!deliveryPostalCodes}
                            onValueChange={(value) => {
                              if (value !== "Other") {
                                form.setValue(
                                  "deliveryAddress.customPostalCode",
                                  ""
                                );
                              }

                              field.onChange(value);

                              if (
                                order.customerType === "MOBI-POSTPAID" ||
                                order.customerType === "FREEDOM"
                              ) {
                                form.setValue(
                                  "deliveryAddress.customPostalCode",
                                  deliveryPostalCodes?.find(
                                    (postalCode) =>
                                      postalCode.id.toString() === value
                                  )?.name
                                );
                              }
                            }}
                            {...field}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={`${t("labels.postal-code")}*`}
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {deliveryPostalCodes?.map((postalCode) => (
                                <SelectItem
                                  key={postalCode.id}
                                  value={postalCode.id.toString()}
                                >
                                  {postalCode.name}
                                </SelectItem>
                              ))}
                              <SelectItem value="Other">
                                {t("labels.other")}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {selectedDeliveryPostalCode === "Other" && (
                      <FormField
                        control={form.control}
                        name="deliveryAddress.customPostalCode"
                        render={({ field }) => (
                          <FormItem className="sm:basis-1/8">
                            <FormLabel className="sr-only">
                              {t("labels.postal-code")}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={`${t("labels.postal-code")}*`}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </div>

                  <FormField
                    control={form.control}
                    name="deliveryAddress.street"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="sr-only">
                          {t("labels.street")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder={`${t("labels.street")}*`}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex flex-col gap-6 sm:flex-row sm:gap-4">
                    <FormField
                      control={form.control}
                      name="deliveryAddress.simpang"
                      render={({ field }) => (
                        <FormItem className="sm:basis-1/3">
                          <FormLabel className="sr-only">
                            {t("labels.simpang")}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={`${t("labels.simpang")}*`}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="deliveryAddress.block"
                      render={({ field }) => (
                        <FormItem className="sm:basis-1/3">
                          <FormLabel className="sr-only">
                            {t("labels.block")}
                          </FormLabel>
                          <FormControl>
                            <Input placeholder={t("labels.block")} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="deliveryAddress.houseNumber"
                      render={({ field }) => (
                        <FormItem className="sm:basis-1/3">
                          <FormLabel className="sr-only">
                            {t("labels.house-number")}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={`${t("labels.house-number")}*`}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="flex flex-col gap-6 sm:flex-row sm:gap-4">
                    <FormField
                      control={form.control}
                      name="deliveryAddress.buildingName"
                      render={({ field }) => (
                        <FormItem className="sm:basis-1/3">
                          <FormLabel className="sr-only">
                            {t("labels.building-name")}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t("labels.building-name")}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="deliveryAddress.floor"
                      render={({ field }) => (
                        <FormItem className="sm:basis-1/3">
                          <FormLabel className="sr-only">
                            {t("labels.floor")}
                          </FormLabel>
                          <FormControl>
                            <Input placeholder={t("labels.floor")} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="deliveryAddress.unit"
                      render={({ field }) => (
                        <FormItem className="sm:basis-1/3">
                          <FormLabel className="sr-only">
                            {t("labels.unit")}
                          </FormLabel>
                          <FormControl>
                            <Input placeholder={t("labels.unit")} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </>
              )}
            </TimelineContent>
          </TimelineItem>

          {serviceType !== "tourist" && serviceType !== "easi" && (
            <TimelineItem step={3}>
              <TimelineHeader>
                <TimelineSeparator className="group-data-[orientation=vertical]/timeline:h-[calc(100%-1.5rem-0.25rem)] group-data-[orientation=vertical]/timeline:translate-y-6.5" />
                <TimelineTitle className="-mt-1">
                  {t("labels.billing-address")}
                </TimelineTitle>
                <TimelineIndicator className="flex size-6 items-center justify-center">
                  <MinusIcon className="size-4" />
                </TimelineIndicator>
              </TimelineHeader>
              <TimelineContent className="space-y-6">
                {(serviceType === "infinity" ||
                  selectedFulfilmentType === "Delivery") && (
                  <FormField
                    control={form.control}
                    name="isSameAsDeliveryAddress"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("labels.is-same-as-delivery-address")}
                        </FormLabel>
                        <FormControl>
                          <div className="relative inline-grid h-6 w-12 grid-cols-[1fr_1fr] items-center font-medium text-sm">
                            <Switch
                              checked={field.value}
                              className="peer absolute inset-0 h-[inherit] w-auto data-[state=unchecked]:bg-input/50 [&_span]:z-10 [&_span]:h-full [&_span]:w-1/2 [&_span]:transition-transform [&_span]:duration-300 [&_span]:ease-[cubic-bezier(0.16,1,0.3,1)] [&_span]:data-[state=checked]:translate-x-full [&_span]:data-[state=checked]:rtl:-translate-x-full"
                              onCheckedChange={field.onChange}
                            />
                            <span className="pointer-events-none relative ms-1 flex items-center justify-center text-center transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] peer-data-[state=checked]:invisible peer-data-[state=unchecked]:translate-x-full peer-data-[state=unchecked]:rtl:-translate-x-full">
                              <XIcon className="size-4" />
                            </span>
                            <span className="pointer-events-none relative me-1 flex items-center justify-center text-center transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] peer-data-[state=unchecked]:invisible peer-data-[state=checked]:-translate-x-full peer-data-[state=checked]:text-background peer-data-[state=checked]:rtl:translate-x-full">
                              <CheckIcon className="size-4" />
                            </span>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {!isSameAsDeliveryAddress && (
                  <div className="flex flex-col gap-6 sm:gap-4">
                    <div className="flex flex-col gap-6 sm:flex-row sm:gap-4">
                      <FormField
                        control={form.control}
                        name="billingAddress.addressCategory"
                        render={({ field }) => (
                          <FormItem className="sm:basis-1/4">
                            <FormLabel className="sr-only">
                              {t("labels.address-category")}
                            </FormLabel>
                            <Select
                              defaultValue={field.value}
                              onValueChange={field.onChange}
                              {...field}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={`${t(
                                      "placeholders.address-category"
                                    )}*`}
                                  />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {ADDRESS_CATEGORIES.map((category) => (
                                  <SelectItem key={category} value={category}>
                                    {category}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="billingAddress.village"
                        render={({ field }) => {
                          const selectedAddressObject =
                            getSelectedBillingAddress();
                          const displayValue = selectedAddressObject
                            ? selectedAddressObject.address
                            : field.value || "";

                          return (
                            <FormItem className="flex flex-col sm:basis-1/2">
                              <FormLabel className="sr-only">
                                {t("labels.village")}
                              </FormLabel>
                              <Popover
                                onOpenChange={(open) => {
                                  setIsBillingAddressCommandOpen(open);
                                  if (!open) {
                                    setBillingAddressQuery("");
                                  }
                                }}
                                open={isBillingAddressCommandOpen}
                              >
                                <PopoverTrigger asChild>
                                  <FormControl>
                                    <InputGroup>
                                      <InputGroupInput
                                        className="w-full pr-8"
                                        onChange={(e) => {
                                          if (isBillingAddressCommandOpen) {
                                            setBillingAddressQuery(
                                              e.target.value
                                            );
                                          }
                                        }}
                                        onClick={() => {
                                          if (!isBillingAddressCommandOpen) {
                                            setIsBillingAddressCommandOpen(
                                              true
                                            );
                                          }
                                        }}
                                        placeholder={`${t(
                                          "placeholders.village"
                                        )}*`}
                                        readOnly={!isBillingAddressCommandOpen}
                                        value={
                                          isBillingAddressCommandOpen
                                            ? billingAddressQuery
                                            : displayValue
                                        }
                                      />
                                      <InputGroupAddon align="inline-end">
                                        {isBillingAddressCommandOpen ? (
                                          <SearchIcon className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground" />
                                        ) : (
                                          <ChevronDownIcon className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground" />
                                        )}
                                      </InputGroupAddon>
                                    </InputGroup>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent
                                  align="start"
                                  className="max-h-64 w-[--radix-popover-trigger-width] overflow-y-auto overflow-x-hidden p-0"
                                  onOpenAutoFocus={(e) => e.preventDefault()}
                                >
                                  {currentBillingAddresses &&
                                  currentBillingAddresses.length > 0 ? (
                                    currentBillingAddresses.map((address) => (
                                      <Button
                                        className="flex h-auto w-full items-center justify-start px-3 py-2 text-left"
                                        key={`${address.district}-${address.mukim}-${address.village}`}
                                        onClick={() => {
                                          form.setValue(
                                            "billingAddress.village",
                                            address.village.toString()
                                          );
                                          form.setValue(
                                            "billingAddress.mukim",
                                            address.mukim.toString()
                                          );
                                          form.setValue(
                                            "billingAddress.district",
                                            address.district.toString()
                                          );
                                          form.setValue(
                                            "billingAddress.address",
                                            address.address
                                          );
                                          setIsBillingAddressCommandOpen(false);
                                          setBillingAddressQuery("");
                                        }}
                                        variant="ghost"
                                      >
                                        <span className="truncate">
                                          {address.address}
                                        </span>
                                      </Button>
                                    ))
                                    // biome-ignore lint/style/noNestedTernary: Cleanest option
                                  ) : billingAddressQuery ? (
                                    <div className="p-4 text-center text-muted-foreground text-sm">
                                      {t("placeholders.no-results")}
                                    </div>
                                  ) : (
                                    <div className="p-4 text-center text-muted-foreground text-sm">
                                      {t("placeholders.search-address")}
                                    </div>
                                  )}
                                </PopoverContent>
                              </Popover>
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />

                      <FormField
                        control={form.control}
                        name="billingAddress.postalCode"
                        render={({ field }) => (
                          <FormItem
                            className={cn(
                              selectedBillingPostalCode === "Other"
                                ? "sm:basis-1/8"
                                : "sm:basis-1/4"
                            )}
                          >
                            <FormLabel className="sr-only">
                              {t("labels.postal-code")}
                            </FormLabel>
                            <Select
                              defaultValue={field.value}
                              disabled={!billingPostalCodes}
                              onValueChange={(value) => {
                                if (value !== "Other") {
                                  form.setValue(
                                    "billingAddress.customPostalCode",
                                    ""
                                  );
                                }

                                field.onChange(value);

                                if (
                                  order.customerType === "MOBI-POSTPAID" ||
                                  order.customerType === "FREEDOM"
                                ) {
                                  form.setValue(
                                    "billingAddress.customPostalCode",
                                    billingPostalCodes?.find(
                                      (postalCode) =>
                                        postalCode.id.toString() === value
                                    )?.name
                                  );
                                }
                              }}
                              {...field}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={`${t("labels.postal-code")}*`}
                                  />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {billingPostalCodes?.map((postalCode) => (
                                  <SelectItem
                                    key={postalCode.id}
                                    value={postalCode.id.toString()}
                                  >
                                    {postalCode.name}
                                  </SelectItem>
                                ))}
                                <SelectItem value="Other">
                                  {t("labels.other")}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {selectedBillingPostalCode === "Other" && (
                        <FormField
                          control={form.control}
                          name="billingAddress.customPostalCode"
                          render={({ field }) => (
                            <FormItem className="sm:basis-1/8">
                              <FormLabel className="sr-only">
                                {t("labels.postal-code")}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder={`${t("labels.postal-code")}*`}
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>

                    <FormField
                      control={form.control}
                      name="billingAddress.street"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="sr-only">
                            {t("labels.street")}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={`${t("labels.street")}*`}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex flex-col gap-6 sm:flex-row sm:gap-4">
                      <FormField
                        control={form.control}
                        name="billingAddress.simpang"
                        render={({ field }) => (
                          <FormItem className="sm:basis-1/3">
                            <FormLabel className="sr-only">
                              {t("labels.simpang")}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={`${t("labels.simpang")}*`}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="billingAddress.block"
                        render={({ field }) => (
                          <FormItem className="sm:basis-1/3">
                            <FormLabel className="sr-only">
                              {t("labels.block")}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t("labels.block")}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="billingAddress.houseNumber"
                        render={({ field }) => (
                          <FormItem className="sm:basis-1/3">
                            <FormLabel className="sr-only">
                              {t("labels.house-number")}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={`${t("labels.house-number")}*`}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="flex flex-col gap-6 sm:flex-row sm:gap-4">
                      <FormField
                        control={form.control}
                        name="billingAddress.buildingName"
                        render={({ field }) => (
                          <FormItem className="sm:basis-1/3">
                            <FormLabel className="sr-only">
                              {t("labels.building-name")}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t("labels.building-name")}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="billingAddress.floor"
                        render={({ field }) => (
                          <FormItem className="sm:basis-1/3">
                            <FormLabel className="sr-only">
                              {t("labels.floor")}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t("labels.floor")}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="billingAddress.unit"
                        render={({ field }) => (
                          <FormItem className="sm:basis-1/3">
                            <FormLabel className="sr-only">
                              {t("labels.unit")}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t("labels.unit")}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                )}
              </TimelineContent>
            </TimelineItem>
          )}
        </Timeline>

        <div className="flex items-center justify-between gap-6">
          <Button onClick={() => navigate(-1)} type="button" variant="outline">
            <ChevronLeftIcon />
            <span>{t("labels.back")}</span>
          </Button>
          <SubmitButton
            className="rounded-md"
            isSubmitting={form.formState.isSubmitting}
          >
            <span>{t("labels.next")}</span>
            <ChevronRightIcon />
          </SubmitButton>
        </div>
      </form>
    </Form>
  );
};
